/**
 * @file publicBrowsing.js
 * @description Config-aware helper for provider-facing public pages.
 *   Guest case browsing is gated by the Admin setting `public_case_browsing`
 *   (default OFF while live case supply is thin). While it is OFF, "Browse
 *   cases" / "Open the Case Finder" buttons land a guest on a signup form,
 *   which is a bait-and-switch. Pages use this hook to swap those buttons for
 *   "Get case alerts" and the free tracker, and to keep the browse buttons
 *   only when the Admin has turned public browsing ON.
 *
 *   Reads through getCachedConfig + useConfigVersion, the same pattern as the
 *   agency pages, so the value is correct on first paint (defaults) and
 *   re-renders when platform_config finishes loading. During prerender
 *   Supabase is blocked, so the build-time default (OFF) is what gets baked in.
 * @module lib/publicBrowsing
 */
import { getCachedConfig } from "./configService";
import { useConfigVersion } from "./useConfigVersion";

/** Synchronous read of the Admin flag (false when unset). */
export function isPublicCaseBrowsingOn() {
  return !!getCachedConfig("public_case_browsing");
}

/**
 * Hook form: re-renders the calling page when Admin config loads.
 * @returns {boolean} true when logged-out visitors may browse the Case Finder
 */
export function usePublicCaseBrowsing() {
  useConfigVersion();
  return isPublicCaseBrowsingOn();
}
